import { useEffect, useRef } from 'react'
import refreshUserPermissions from 'services/collaborator/refreshUserPermissions'
import { updateUserCachedData, getUserCachedData } from 'utils/UserAuthorization'

/**
 * Hook para sincronizar a role do usuário periodicamente
 * Verifica a cada 5 minutos se a role foi alterada no banco de dados
 * Se mudou, atualiza automaticamente o localStorage
 * 
 * @param {boolean} enabled - Se o hook deve estar ativo (geralmente quando isLogged === true)
 */
export const useSyncUserRole = (enabled = true) => {
  const intervalRef = useRef(null)
  const lastRoleRef = useRef(null)

  useEffect(() => {
    if (!enabled) return

    // Inicializa com a role atual
    const currentUser = getUserCachedData()
    if (currentUser) {
      lastRoleRef.current = currentUser.userRole || currentUser.role
    }

    // Função para verificar se a role mudou
    const checkRoleSync = async () => {
      try {
        const freshUserData = await refreshUserPermissions()
        if (!freshUserData) return

        const newRole = freshUserData.userRole || freshUserData.role
        const currentRole = lastRoleRef.current

        // Se a role mudou, atualizar localStorage
        if (newRole && newRole !== currentRole) {
          console.log(
            `🔄 Role sincronizada: ${currentRole} → ${newRole}`
          )
          updateUserCachedData(freshUserData)
          lastRoleRef.current = newRole

          // Disparar evento customizado para notificar outros componentes
          window.dispatchEvent(
            new CustomEvent('userRoleChanged', {
              detail: { oldRole: currentRole, newRole: newRole },
            })
          )
        }
      } catch (error) {
        console.error('❌ Erro ao sincronizar role do usuário:', error)
      }
    }

    // Configurar intervalo de sincronização (5 minutos = 300000ms)
    intervalRef.current = setInterval(checkRoleSync, 5 * 60 * 1000)

    // Fazer uma verificação inicial imediatamente
    checkRoleSync()

    // Limpar intervalo ao desmontar
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [enabled])
}

export default useSyncUserRole
